<template>
  <section>
    <template v-for="(day, i) in days" :key="i">
      <ItineraryTimelineDay class="item heading" :date="startsAt" :day="day.dayNumber" :title="day.title" />

      <template v-for="(activity, idx) in day.activities" :key="idx">
        <div v-if="isShowableTransportation(activity)" :class="activity.transportationType" class="item transport">
          <span class="badge">
            <span class="transport-time">~ {{ humanizeDuration(activity.durationLength, activity.durationUnit) }}</span>
            {{ activity.transportationType }}
          </span>
        </div>

        <div v-else-if="activity.type !== 'transportation'" class="item activity">
          {{ activity.shortTitle || activity.title }}
        </div>
      </template>
    </template>
  </section>
</template>

<script setup>
import ItineraryTimelineDay from '@/modules/Itinerary/Components/ItineraryTimelineDay.vue'
import { humanizeDuration } from '@/utils/dateUtils.ts'

defineProps({
  days: Array,
  startsAt: String,
})

const isShowableTransportation = activity => {
  return (
    activity.type === 'transportation' &&
    activity.transportationType &&
    activity.durationLength &&
    activity.durationUnit
  )
}
</script>

<style lang="scss" scoped>
$line-width: 2px;

.item {
  position: relative;
  padding-block: 8px;
  padding-left: 24px;

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    content: '';
    border-left: $line-width solid $brand-tertiary-light;
  }

  &:first-child::before {
    /* Hide line overflowing first bullet */
    top: 50%;
    height: 50%;
  }

  &:last-child::before {
    height: 50%;
  }

  /* Hide line overflowing last bullet */
  &::after {
    position: absolute;
    top: 50%;
    left: $line-width / 2; /* Center bullets/icons on line */
    z-index: 2;
    content: '';
    transform: translate(-50%, -50%);
  }

  &.heading::after,
  &.activity::after {
    border: 1px solid $brand-tertiary;
    border-radius: 50%;
  }

  &.heading::after {
    width: 8px;
    height: 8px;
    background-color: $brand-tertiary;
  }

  &.activity::after {
    width: 0.375rem;
    height: 0.375rem;
    background-color: $white;
  }

  &.transport {
    .badge {
      padding: 0.25rem 0.5rem;
      font-size: 0.75rem;
      border: 1px solid $green-300;
      border-radius: 0.75rem;
      background-color: #fff;
    }

    .transport-time {
      font-weight: 600;
    }

    &::after {
      font-family: 'streamline';
      font-size: 0.875rem;
      color: $green-300;
    }
  }
}

.heading {
  color: $brand-primary;
  font-weight: 600;
}

.transport.boat::after {
  content: '\e82f';
}
.transport.bus::after {
  content: '\e828';
}
.transport.car-driver::after {
  content: '\e82b';
}
.transport.car::after {
  content: '\e827';
}
.transport.ferry::after {
  content: '\e817';
}
.transport.motorcycle::after {
  content: '\e82d';
}
.transport.plane::after {
  content: '\e80e';
}
.transport.self-drive::after {
  content: '\e827';
}
.transport.speedboat::after {
  content: '\e82f';
}
.transport.taxi::after {
  content: '\e831';
}
.transport.train::after {
  content: '\e80f';
}
.transport.van::after {
  content: '\e834';
}
.transport.walk::after {
  content: '\e82c';
}
</style>
